import classNames from 'classnames';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';

import PlaceholderCard from 'components/CustomCollectionProductCard/PlaceholderCard';
import { Medium, Small } from 'components/MediaQuery';
import { COLLECTION_N_COLUMNS, CUSTOM_COLLECTION_ITEM_TYPES } from 'constants/collection';
import { COLLECTION_COLORWAY_PICKER_APPEARANCES } from 'constants/collectionPage';
import { largeUp, medium } from 'constants/mediaQueries';
import CustomCollectionColorwayCard from 'containers/CustomCollectionColorwayCardContainer';
import CustomCollectionGridSizeSelectorContainer from 'containers/CustomCollectionGridSizeSelectorContainer';
import CustomCollectionMasterProductCard from 'containers/CustomCollectionMasterProductCardContainer';
import CustomCollectionStoryCard, {
  ORIENTATION,
} from 'containers/CustomCollectionStoryCardContainer';
import Theme from 'styles/theme';

import SubHeader from './SubHeader';

const getWidthPercentage = ({ nColumns, span }) => (100 / nColumns) * span;

const Grid = ({ className, nColumns, rows, appearance, hasMobileQuickAddRow }) => {
  return (
    <div className={classNames('Grid', `Grid--${nColumns}-column`, className)}>
      {map(rows, (row, i) => (
        <Fragment key={`row-${i}`}>
          <div className="Grid__row">
            {map(row, (item, j) => (
              <div
                className={classNames(
                  'Grid__item',
                  `Grid__item--type-${item.type}`,
                  `Grid__item--span-${item.columnSpan}`,
                  `Grid__item--row-${i}`
                )}
                key={`item--${item.id}-${j}`}
              >
                {item.type === CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT && (
                  <CustomCollectionMasterProductCard
                    masterHandle={item.id}
                    appearance={appearance}
                    nItemsPerSlide={item.columnSpan === 2 || nColumns !== 2 ? 6 : 4}
                    hasMobileQuickAddRow={hasMobileQuickAddRow}
                  />
                )}
                {item.type === CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY && (
                  <CustomCollectionColorwayCard
                    productHandle={item.id}
                    hasMobileQuickAddRow={hasMobileQuickAddRow}
                  />
                )}
                {item.type === CUSTOM_COLLECTION_ITEM_TYPES.STORY_CARD && (
                  <CustomCollectionStoryCard
                    storyId={item.id}
                    orientation={
                      item.columnSpan === 2 || nColumns === 1
                        ? ORIENTATION.LANDSCAPE
                        : ORIENTATION.PORTRAIT
                    }
                  />
                )}
                {item.type === CUSTOM_COLLECTION_ITEM_TYPES.SUB_HEADER && (
                  <div className="Grid__item--type-SUB_HEADER-child">
                    <SubHeader text={item.data} />
                  </div>
                )}
                {item.type === CUSTOM_COLLECTION_ITEM_TYPES.PLACEHOLDER && <PlaceholderCard />}
              </div>
            ))}
          </div>

          {hasMobileQuickAddRow ? (
            <>
              <Small>
                <div className="Grid__row Grid__size-selector-row" key={`size-selector-row-${i}`}>
                  {map(row, (item, j) => (
                    <div className="Grid__size-selector-row__item" key={`size-selector-item--${j}`}>
                      {(item.type === CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY ||
                        item.type === CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT) && (
                        <CustomCollectionGridSizeSelectorContainer
                          productHandle={item.id}
                          isMasterProduct={
                            item.type === CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT
                          }
                          key={`size-selector-item--${j}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </Small>

              <Medium>
                <div className="Grid__row Grid__size-selector-row" key={`size-selector-row-${i}`}>
                  {map(row, (item, j) => (
                    <div className="Grid__size-selector-row__item" key={`size-selector-item--${j}`}>
                      {(item.type === CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY ||
                        item.type === CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT) && (
                        <CustomCollectionGridSizeSelectorContainer
                          productHandle={item.id}
                          isMasterProduct={
                            item.type === CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT
                          }
                          key={`size-selector-item--${j}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </Medium>
            </>
          ) : null}
        </Fragment>
      ))}
      <style jsx>
        {`
          .Grid {
            padding: ${Theme.get('spacing') * 4}px 0;
            width: 100%;
            background-color: ${Theme.get('colors-black-10')};
          }

          .Grid__row {
            display: flex;
            width: 100%;
            padding-left: ${Theme.get('spacing') * 4}px;
          }

          .Grid__size-selector-row {
            display: grid;
            grid-template-columns: repeat(${nColumns}, 1fr);
            width: 100%;
            margin-bottom: ${Theme.get('spacing') * 2}px;
          }

          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__row {
            padding-left: 0;
            margin-bottom: ${Theme.get('spacing') * 6}px;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__row {
            padding-left: ${Theme.get('spacing') * 2}px;
          }

          .Grid__item {
            display: inline-block;
            max-width: 100%;
            padding: 0 ${Theme.get('spacing') * 4}px 0 0;
            flex-shrink: 0;
          }

          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item {
            padding-right: ${Theme.get('spacing') * 2}px;
            padding-left: ${Theme.get('spacing') * 2}px;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__item {
            padding-right: ${Theme.get('spacing') * 2}px;
          }

          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item--type-COLORWAY,
          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item--type-MASTER_PRODUCT,
          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item--type-SUB_HEADER,
          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item--type-PLACEHOLDER {
            margin: 0 auto;
            max-width: ${Theme.get('spacing') * 54}px;
          }

          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item--type-STORY_CARD {
            padding: 0px;
          }

          .Grid__item--type-SUB_HEADER {
            width: 100%;
          }

          .Grid__item--type-SUB_HEADER:before {
            content: '';
            display: block;
            border-top: 2px solid ${Theme.get('colors-black-20')};
            margin-top: ${Theme.get('spacing') * 4}px;
          }

          .Grid__item--row-0.Grid__item--type-SUB_HEADER:before {
            margin-top: 0;
            border: none;
          }

          .Grid--${COLLECTION_N_COLUMNS.FOUR}-column .Grid__item--span-1 {
            width: ${getWidthPercentage({ nColumns: 4, span: 1 })}%;
          }

          .Grid--${COLLECTION_N_COLUMNS.FOUR}-column .Grid__item--type-SUB_HEADER-child,
          .Grid--${COLLECTION_N_COLUMNS.FOUR}-column .Grid__item--span-2 {
            width: ${getWidthPercentage({ nColumns: 4, span: 2 })}%;
          }

          .Grid--3-column .Grid__item--span-1 {
            width: ${getWidthPercentage({ nColumns: 3, span: 1 })}%;
          }

          .Grid--3-column .Grid__item--span-2 {
            width: ${getWidthPercentage({ nColumns: 3, span: 2 })}%;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__item--span-1 {
            width: ${getWidthPercentage({ nColumns: 2, span: 1 })}%;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__item--span-2 {
            width: ${getWidthPercentage({ nColumns: 2, span: 2 })}%;
          }

          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__item {
            width: ${getWidthPercentage({ nColumns: 1, span: 1 })}%;
          }

          @media ${medium} {
            .Grid--${COLLECTION_N_COLUMNS.THREE}-column .Grid__size-selector-row {
              column-gap: 0;
            }
          }

          @media ${largeUp} {
            .Grid {
              background-color: ${Theme.get('colors-white')};
            }

            .Grid__row {
              margin-bottom: ${Theme.get('spacing') * 4}px;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .Grid--${COLLECTION_N_COLUMNS.ONE}-column .Grid__row .HoverCard.HoverCard--active::before,
          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .Grid__row .HoverCard.HoverCard--active::before,
          .Grid--3-column .Grid__row .HoverCard.HoverCard--active::before {
            box-shadow: none;
          }

          .Grid--${COLLECTION_N_COLUMNS.TWO}-column
            .MasterProductCard
            .ColorwayPicker--color-swatches {
            padding: 0 ${Theme.get('spacing') * 2}px;
            padding-bottom: ${Theme.get('spacing-px')};
          }

          .Grid__size-selector-row .MobileSizeSelector .MobileSizeSelector__grid-item {
            width: 25%;
          }

          .Grid--${COLLECTION_N_COLUMNS.THREE}-column .ColorwayCard,
          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .ColorwayCard {
            display: flex;
            flex-direction: column;
          }

          .Grid--${COLLECTION_N_COLUMNS.THREE}-column .ColorwayCard .ShadowBox,
          .Grid--${COLLECTION_N_COLUMNS.TWO}-column .ColorwayCard .ShadowBox {
            flex-grow: 1;
          }

          .Grid__size-selector-row .MobileSizeSelector {
            margin-right: ${Theme.get('spacing') * 2}px;
          }

          @media ${medium} {
            .Grid__size-selector-row .MobileSizeSelector .MobileSizeSelector__grid-item {
              width: 20%;
            }

            .Grid--${COLLECTION_N_COLUMNS.THREE}-column .Grid__row .StoryCard {
              min-height: auto;
            }

            .Grid--${COLLECTION_N_COLUMNS.THREE}-column
              .Grid__size-selector-row
              .MobileSizeSelector {
              margin-right: ${Theme.get('spacing') * 4}px;
            }
          }
        `}
      </style>
    </div>
  );
};

Grid.propTypes = {
  className: PropTypes.string,
  nColumns: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        columnSpan: PropTypes.number.isRequired,
      })
    )
  ).isRequired,
  appearance: PropTypes.oneOf(Object.values(COLLECTION_COLORWAY_PICKER_APPEARANCES)).isRequired,
  hasMobileQuickAddRow: PropTypes.bool,
};

Grid.defaultProps = {
  className: '',
};

export default Grid;
